/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { Button, ErrorBoundary, Grid, Spacer, Table } from "@cd2/components"
import { Modal, TextGroupMedium } from "@cd2/components/build/es6"
import { P } from "@cd2/text"
import { IComponentCallback } from "@cd2/tools/build/es6"
import React, { useEffect, useState } from "react"
import { ConfirmCancelButtons } from "../../Components/Buttons/ConfirmCancelButtons"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import {
  ProductForm,
  productFormLoadProductMasterData,
} from "../../Pages/Stock/Products/ProductForm"
import { ProductSelect } from "../../stories/quoteOrders/ItemRowSageProduct"
import { itemTypes } from "../../stories/quoteOrders/quoteOrderUtils"
import { IRecord } from "../../types/schema"
import { useSettings } from "../../utils/useSettings"
import { useConfirmation } from "../useConfirmation"
import { useSiteAlerts } from "../useSiteAlerts"
import { ErrorBoundaryInModal } from "../../Components/ErrorModalMessage"
import {
  handleNotifyNewExportedProduct,
  handleSkeemCreateProduct,
} from "../../Pages/Stock/Products/ProductsNewPage"

type IProduct = IRecord<
  "products",
  "body" | "category" | "stock_code" | "cost_price_pennies" | "deleted"
> & {
  product_locations: IRecord<"product_locations", "name" | "free_stock">[]
}

type IItem = IRecord<
  "quote_items",
  | "stock_code"
  | "stock_location"
  | "body"
  | "category"
  | "cost_price_pennies"
  | "price_pennies"
  | "quantity"
  | "created_at"
  | "updated_at"
  | "weight"
  | "type"
  | "markUp"
> & { product: IProduct }

export function useAssignItemsToCustomProducts(): [
  React.ReactNode,
  (data: IItem[]) => Promise<boolean>,
] {
  const [confirmNode, getConfirmation] = useConfirmation(
    ({ data, onResolve }: { data: IItem[]; onResolve(value: boolean): void }) => {
      const { currentUser } = useGlobalState()
      const skeem = window.skeem
      const [items, setItems] = useState(data.filter(item => item.type === itemTypes.custom))
      const [isCreating, setIsCreating] = useState<(typeof items)[number] | null>(null)

      function updateItem(itemId: string, data: Partial<(typeof items)[0]>) {
        setItems(items => items.map(item => (item.id === itemId ? { ...item, ...data } : item)))
      }

      async function handleChooseProduct(itemId: string, productId: string) {
        const product = await skeem.fetch2<IProduct>({
          first: {
            name: "products",
            attributes: [
              "stock_code",
              "body",
              "category",
              "cost_price_pennies",
              { name: "branch", attributes: ["name"] },
              { name: "product_locations", attributes: ["name", "free_stock"] },
            ],
            filter: { matches: { id: productId } },
          },
        })
        updateItem(itemId, { product })
      }

      async function handleSubmit(onResolve: (value: boolean) => void) {
        const mutations = items
          .filter(item => !!item.product)
          .map(item => ({
            update: {
              id: item.id,
              product: { set: item.product.id },
              product_location: { set: item.product.product_locations?.[0]?.id ?? null },
              type: itemTypes.sage,
            },
          }))
        if (mutations.length > 0) {
          await skeem.mutate({ quote_items: mutations })
        }
        onResolve(true)
      }

      return (
        <ErrorBoundary ErrorComponent={() => <div>asd</div>}>
          <Modal
            width={800}
            render={() => (
              <Grid rows={["max-content", { fr: 1 }, "max-content"]} gap={16}>
                <TextGroupMedium
                  heading="Assign products"
                  body="Select or create new Sage products to assign to your custom products."
                />
                {isCreating ? (
                  <CreateProductForm
                    data={isCreating}
                    onCancel={() => setIsCreating(null)}
                    onSubmit={async productId => {
                      await handleChooseProduct(isCreating.id, productId)
                      setIsCreating(null)
                    }}
                  />
                ) : (
                  <React.Fragment>
                    {items.length > 0 && (
                      <div
                        css={css`
                          max-height: 400px;
                          overflow: auto;
                        `}
                      >
                        <FloatingBox
                          css={css`
                            ${FloatingBox.vars.childPadding}: 8px;
                          `}
                          color="#f5f5f5"
                        >
                          <P>
                            Adding a product to an item will convert this item from a custom product
                            in to a Sage Product item.
                          </P>
                          <Spacer />
                          <div>
                            <Table data={items}>
                              <Table.Column heading="Item">
                                {item => (
                                  <div>
                                    <P>
                                      <b>{item.stock_code}</b>
                                      {item.body && (
                                        <React.Fragment>
                                          <br />
                                          {item.body}
                                        </React.Fragment>
                                      )}
                                    </P>
                                  </div>
                                )}
                              </Table.Column>
                              {currentUser.canCreateSageProducts && (
                                <Table.Column
                                  css={css`
                                    max-width: 86px;
                                  `}
                                >
                                  {item => (
                                    <Button
                                      extendTheme={{ padding: 8 }}
                                      onClick={() => setIsCreating(item)}
                                    >
                                      Create sage product
                                    </Button>
                                  )}
                                </Table.Column>
                              )}
                              <Table.Column>
                                {item => (
                                  <ProductSelect
                                    value={item.product}
                                    onChange={product => handleChooseProduct(item.id, product.id)}
                                    disabled={item.toDelete}
                                  />
                                )}
                              </Table.Column>
                            </Table>
                          </div>
                        </FloatingBox>
                      </div>
                    )}
                    <div>
                      <ConfirmCancelButtons
                        onConfirm={() => handleSubmit(onResolve)}
                        onCancel={() => onResolve(false)}
                        buttonOpts={{
                          confirm: { disabled: !items.some(item => item.product) },
                        }}
                      />
                    </div>
                  </React.Fragment>
                )}
              </Grid>
            )}
            isOpen
          />
        </ErrorBoundary>
      )
    },
  )

  return [confirmNode, getConfirmation]
}

export function CreateProductForm({
  data,
  onCancel,
  onSubmit,
}: {
  data: IItem
  onCancel: () => void
  onSubmit: (productId: string) => Promise<void> | void
}) {
  const { defaultStockLocation, _loaded } = useSettings()
  const siteAlerts = useSiteAlerts()
  const skeem = window.skeem
  const { currentUser } = useGlobalState()
  const [preloadedData, setPreloadedData] = useState<any>(null)
  const [exactStockCodeMatch, setExactStockCodeMatch] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  async function loadInitialData() {
    if (!currentUser.branch.id || !_loaded) {
      return
    }
    setIsLoading(true)
    try {
      const warehouse = await skeem.fetch2<IRecord<"warehouses", "name">>({
        first: {
          name: "warehouses",
          attributes: ["name"],
          filter: { matches: { name: defaultStockLocation, branch: currentUser.branch.id } },
        },
      })
      const productMaster = await skeem.fetch2<IRecord<"product_masters", "name" | "stock_code">[]>(
        {
          model: {
            name: "product_masters",
            attributes: ["name", "stock_code"],
            filter: {
              and: [
                {
                  eq: [
                    { lowercase: { attr: "stock_code" } },
                    { value: data.stock_code.toLowerCase() },
                  ],
                },
                {
                  not: {
                    exists: {
                      path: [
                        "products",
                        { name: "branch", filter: { matches: { id: currentUser.branch.id } } },
                      ],
                    },
                  },
                },
              ],
            },
          },
        },
      )
      // if only 1 result, just use that
      if (productMaster.length === 1) {
        const result = await productFormLoadProductMasterData(
          skeem,
          currentUser.branch,
          productMaster[0],
        )
        setPreloadedData({
          ...result,
          preferred_supplier: result.supplier ? { id: result.supplier } : null,
          product_group: result.product_group ? { id: result.product_group } : null,
          supplierStockCode: result.supplier_part_number,
          body: data.body ?? result.body,
          stock_code: data.stock_code,
          warehouse: warehouse,
        })
        setExactStockCodeMatch(
          !!(result && data.stock_code && result.stock_code === data.stock_code),
        )
        return
      } else if (productMaster.length > 1) {
        // if more than 1 result, load the data (except for building manufacturers)
        // pick the one with the most values
        const productMasterData = await Promise.all(
          productMaster.map(p => productFormLoadProductMasterData(skeem, currentUser.branch, p)),
        )
        const pmsSorted = productMasterData.sort((a, b) => {
          const valuesA = Object.values(a).filter(Boolean).length
          const valuesB = Object.values(b).filter(Boolean).length
          return valuesB - valuesA
        })
        const pmWithMostValues = pmsSorted[0]
        if (pmWithMostValues.product_master) {
          const result = await productFormLoadProductMasterData(
            skeem,
            currentUser.branch,
            pmWithMostValues.product_master,
          )
          setPreloadedData({
            ...result,
            preferred_supplier: result.supplier ? { id: result.supplier } : null,
            product_group: result.product_group ? { id: result.product_group } : null,
            supplierStockCode: result.supplier_part_number,
            body: data.body ?? result.body,
            stock_code: data.stock_code,
            warehouse: warehouse,
          })
          setExactStockCodeMatch(
            !!(result && data.stock_code && result.stock_code === data.stock_code),
          )
          return
        }
      }
      setPreloadedData({
        stock_code: data.stock_code,
        cost_price_pennies: data.cost_price_pennies,
        name: data.body,
        warehouse: warehouse,
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadInitialData()
  }, [currentUser?.branch?.id, _loaded, data.id, data.stock_code])

  async function handleCreateProduct(data: IComponentCallback<typeof ProductForm, "onSubmit">) {
    try {
      const newRecord = await handleSkeemCreateProduct(skeem, data, currentUser)
      siteAlerts.genericSuccess("Product created")
      await handleNotifyNewExportedProduct(skeem, newRecord.id, currentUser, {
        newProduct: !data.product_master?.id,
        exporting: data.exportOnSave,
      })
      await onSubmit(newRecord.id)
    } catch (e) {
      console.error(e)
      siteAlerts.genericError("Error creating product")
    }
  }

  return isLoading ? null : (
    <ErrorBoundaryInModal onClose={onCancel}>
      <ProductForm
        onSubmit={handleCreateProduct}
        data={preloadedData}
        canEditProductMaster={!exactStockCodeMatch}
        disabledEditProductMasterReason={
          exactStockCodeMatch
            ? "This product master was automatically selected as it exactly matches the stock code you entered."
            : undefined
        }
        onCancel={onCancel}
        isNew
      />
    </ErrorBoundaryInModal>
  )
}
